import { useMemo, useState } from 'react';
import { Check, Loader2, Plus, X } from 'lucide-react';
import { COMMAND_CENTER_FLAG_DEFAULTS } from '@oneup/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useFieldOptions } from '@/lib/use-field-options';
import { cn } from '@/lib/utils';

interface TaskTagsControlProps {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  compact?: boolean;
}

export function TaskTagsControl({
  value,
  onChange,
  disabled,
  compact = false,
}: TaskTagsControlProps) {
  const [draft, setDraft] = useState('');
  const optionsQuery = useFieldOptions('Command Center Flags');
  const tags = useMemo(
    () => mergeTagOptions(value, optionsQuery.data?.options.map((option) => option.name) ?? []),
    [optionsQuery.data?.options, value],
  );

  const toggleTag = (tag: string) => {
    if (disabled) return;
    const selected = value.includes(tag);
    onChange(selected ? value.filter((item) => item !== tag) : [...value, tag]);
  };

  const addTag = () => {
    const tag = normalizeDraftTag(draft);
    if (!tag || value.includes(tag) || disabled) return;
    onChange([...value, tag]);
    setDraft('');
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          onClick={(event) => event.stopPropagation()}
          className={cn(
            'inline-flex min-h-8 max-w-full items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-left transition-colors hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-60',
            compact ? 'w-[190px]' : 'w-full',
          )}
        >
          {disabled && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
          <span className="flex min-w-0 flex-1 flex-wrap gap-1">
            {value.length === 0 ? (
              <span className="text-[12px] text-muted-foreground">No flags</span>
            ) : (
              value.slice(0, compact ? 2 : 4).map((tag) => (
                <Badge key={tag} variant={tagVariant(tag)} className="max-w-[120px] truncate">
                  {tag}
                </Badge>
              ))
            )}
            {value.length > (compact ? 2 : 4) && (
              <Badge variant="outline">+{value.length - (compact ? 2 : 4)}</Badge>
            )}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[280px] space-y-3 p-3"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
            Command Center Flags
          </div>
          <div className="grid gap-1.5">
            {tags.map((tag) => {
              const selected = value.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={cn(
                    'flex h-8 items-center justify-between gap-2 rounded-md border px-2 text-[13px] transition-colors',
                    selected
                      ? 'border-primary/40 bg-primary/10 text-primary'
                      : 'border-border hover:bg-muted/50',
                  )}
                >
                  <span className="truncate">{tag}</span>
                  {selected ? <Check className="h-3.5 w-3.5" aria-hidden /> : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex gap-1.5">
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addTag();
              }
            }}
            placeholder="Add tag"
            className="h-8 text-[13px]"
          />
          <Button type="button" size="icon" className="h-8 w-8" onClick={addTag} disabled={!draft.trim()}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>

        {value.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-full gap-1.5"
            onClick={() => onChange([])}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Clear flags
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function mergeTagOptions(current: string[], schemaOptions: string[]): string[] {
  const seen = new Set<string>();
  const merged: string[] = [];
  for (const tag of [...COMMAND_CENTER_FLAG_DEFAULTS, ...schemaOptions, ...current]) {
    if (!tag || seen.has(tag)) continue;
    seen.add(tag);
    merged.push(tag);
  }
  return merged;
}

function normalizeDraftTag(value: string): string {
  return value.trim().replace(/\s+/g, ' ').slice(0, 60);
}

function tagVariant(tag: string): 'destructive' | 'warning' | 'info' | 'success' | 'outline' {
  if (tag === 'On Fire') return 'destructive';
  if (tag === '<5m') return 'success';
  if (tag === 'Send To Client') return 'warning';
  if (tag === 'Shortlist') return 'info';
  return 'outline';
}
